import React, { useState } from 'react';
import { formatDate } from '@/lib/dateUtils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Download, Edit2, Building2, Pen, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';
import { SignaturePad } from './SignaturePad';

/**
 * Draw Request Preview Modal
 * Shows a preview of the Hills Bank draw request form before generating Excel
 */
export const DrawRequestPreviewModal = ({
  isOpen,
  onClose,
  project,
  selectedItems,
  holdingCosts,
  onEditProject,
  onGenerate,
  isGenerating,
  signDocument,
  setSignDocument,
  userSignature,
  setUserSignature,
  userId,
  excelOnly,
  setExcelOnly,
  formStyle = 'hills',
  setFormStyle = () => {},
  onCompare = () => {},
  isComparing = false,
  customAvailable = false,
  bankName = null,
  onSubmitToBank = null,
  defaultReplyTo = '',
  isSubmittingToBank = false
}) => {
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [isSavingSignature, setIsSavingSignature] = useState(false);
  const [lenderEmail, setLenderEmail] = useState(project?.lender_email || '');
  const [replyToEmail, setReplyToEmail] = useState(defaultReplyTo || '');
  const [testMode, setTestMode] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  
  // Note: data-testid attributes added for testing
  if (!project) return null;
  
  // Handle saving signature from pad
  const handleSaveSignature = async (signatureData) => {
    if (!signatureData) {
      toast.error('Please draw your signature first');
      return;
    }
    
    setIsSavingSignature(true);
    try {
      await apiService.uploadSignature(userId, signatureData);
      setUserSignature(signatureData);
      // Auto-enable signing once a signature is drawn so it transfers onto the
      // form — users otherwise miss the separate checkbox below.
      setSignDocument(true);
      setShowSignaturePad(false);
      toast.success('Signature saved — this draw will be signed');
    } catch (error) {
      console.error('Error saving signature:', error);
      toast.error('Failed to save signature');
    } finally {
      setIsSavingSignature(false);
    }
  };
  
  // Handle signature deletion
  const handleDeleteSignature = async () => {
    try {
      await apiService.deleteSignature(userId);
      setUserSignature(null);
      setSignDocument(false);
      toast.success('Signature removed');
    } catch (error) {
      console.error('Error deleting signature:', error);
      toast.error('Failed to remove signature');
    }
  };
  
  // Calculate total
  const itemsTotal = selectedItems.reduce((sum, item) => sum + (item.amount || 0), 0);
  const holdingTotal = holdingCosts.reduce((sum, hc) => sum + (hc.amount || 0), 0);
  const totalRequested = itemsTotal + holdingTotal;
  
  // Get bank contacts
  const bankContacts = project.bank_contacts || [];
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="sm:max-w-[700px] max-h-[90vh] flex flex-col p-0"
        data-testid="draw-preview-modal"
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        {/* Header styled like the Hills Bank paper form */}
        <div className="bg-white border-b-2 border-slate-200 p-4 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-end gap-[3px]" aria-hidden>
                <div className="w-2.5 h-8 bg-[#5c9e33] rounded-sm" />
                <div className="w-2.5 h-8 bg-[#5c9e33] rounded-sm -ml-[3px]" />
              </div>
              <div>
                <DialogTitle className="text-[#1b4b8a] text-xl font-extrabold tracking-tight leading-none">
                  {(project.bank_name || 'HILLS BANK').toUpperCase()}
                </DialogTitle>
                <DialogDescription className="text-slate-500 text-xs mt-0.5">
                  Request for Draw and Lien Guaranty Form
                </DialogDescription>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="hidden sm:block text-sm font-semibold text-slate-700 text-right max-w-[220px]">
                Request for Draw and Lien Guaranty Form
              </span>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onEditProject}
                data-testid="draw-edit-details-btn"
              >
                <Edit2 className="w-4 h-4 mr-1" />
                Edit Details
              </Button>
            </div>
          </div>
        </div>
        
        {/* Scrollable content area - using overflow-auto for better mobile support */}
        <div className="flex-1 overflow-y-auto max-h-[65vh] overscroll-contain">
          <div className="p-4 space-y-4">
            {/* Form format selector */}
            <div className="rounded-lg border border-slate-200 p-3" data-testid="draw-format-selector">
              <h4 className="font-semibold text-slate-700 text-sm mb-2">Form format</h4>
              <div className={`grid gap-2 ${customAvailable ? 'grid-cols-3' : 'grid-cols-2'}`}>
                {customAvailable && (
                  <button
                    type="button"
                    onClick={() => setFormStyle('custom')}
                    data-testid="form-style-custom"
                    className={`text-left rounded-lg border-2 p-3 transition ${formStyle === 'custom' ? 'border-purple-600 bg-purple-50' : 'border-slate-200 hover:border-slate-300'}`}
                  >
                    <p className="text-sm font-bold text-slate-900">Your bank&apos;s form</p>
                    <p className="text-xs text-slate-500 mt-0.5">Filled onto your uploaded PDF</p>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setFormStyle('hills')}
                  data-testid="form-style-hills"
                  className={`text-left rounded-lg border-2 p-3 transition ${formStyle === 'hills' ? 'border-green-500 bg-green-50' : 'border-slate-200 hover:border-slate-300'}`}
                >
                  <p className="text-sm font-bold text-slate-900">{(bankName || project.bank_name || 'Hills Bank')} format</p>
                  <p className="text-xs text-slate-500 mt-0.5">Auto-generated draw form</p>
                </button>
                <button
                  type="button"
                  onClick={() => setFormStyle('drawready')}
                  data-testid="form-style-drawready"
                  className={`text-left rounded-lg border-2 p-3 transition ${formStyle === 'drawready' ? 'border-blue-600 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}
                >
                  <p className="text-sm font-bold text-slate-900">DrawReady Standard</p>
                  <p className="text-xs text-slate-500 mt-0.5">Clean universal draw form</p>
                </button>
              </div>
            </div>

            {/* Borrower Information */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                <h4 className="font-semibold text-slate-700 text-sm uppercase tracking-wide">
                  Borrower Information
                </h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Borrower:</span>
                    <span className="font-medium text-slate-900">
                      {project.borrower_entity || project.borrower || project.name || '-'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Address:</span>
                    <span className="font-medium text-slate-900">{project.address || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">City:</span>
                    <span className="font-medium text-slate-900">{project.city || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Short Legal:</span>
                    <span className="font-medium text-slate-900">{project.short_legal || '-'}</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                <h4 className="font-semibold text-slate-700 text-sm uppercase tracking-wide">
                  Loan Information
                </h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Loan #:</span>
                    <span className="font-medium text-slate-900">{project.loan_number || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Draw Account:</span>
                    <span className="font-medium text-slate-900">{project.draw_account || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Bank:</span>
                    <span className="font-medium text-slate-900">{project.bank_name || 'Hills Bank'}</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* FOR BANK USE ONLY */}
            <div className="bg-gray-100 rounded-lg p-3 border border-gray-200">
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-semibold text-gray-500 text-xs uppercase tracking-wide mb-2">
                    For Bank Use Only
                  </h4>
                  <div className="text-xs text-gray-400 space-y-1">
                    <p>Processed by: _______________</p>
                    <p>Date: _______________</p>
                  </div>
                </div>
                {bankContacts.length > 0 && (
                  <div className="text-xs text-gray-500">
                    {bankContacts.map((contact, idx) => (
                      <p key={idx}>
                        {idx + 1}. {contact.name} {contact.phone && `- ${contact.phone}`}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Draw Items Table */}
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left p-2 font-semibold text-slate-700 w-12">#</th>
                    <th className="text-left p-2 font-semibold text-slate-700">Name of Subcontractor/Supplier</th>
                    <th className="text-left p-2 font-semibold text-slate-700 w-20">Category #</th>
                    <th className="text-left p-2 font-semibold text-slate-700">Category</th>
                    <th className="text-right p-2 font-semibold text-slate-700 w-28">Amount Paid</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedItems.map((item, idx) => (
                    <tr key={item.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-2 text-slate-600">{idx + 1}</td>
                      <td className="p-2 text-slate-900">{item.vendor || item.contractor || '-'}</td>
                      <td className="p-2 text-slate-600 font-mono">{item.categoryCode || '-'}</td>
                      <td className="p-2 text-slate-700">{item.categoryName || '-'}</td>
                      <td className="p-2 text-right font-medium text-slate-900">
                        ${(item.amount || 0).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                  
                  {/* Holding Costs */}
                  {holdingCosts.length > 0 && (
                    <>
                      <tr className="bg-purple-50">
                        <td colSpan={5} className="p-2 font-semibold text-purple-700 text-xs uppercase">
                          Holding Costs
                        </td>
                      </tr>
                      {holdingCosts.map((hc, idx) => (
                        <tr key={hc.id} className="bg-purple-50/50">
                          <td className="p-2 text-slate-600">{selectedItems.length + idx + 1}</td>
                          <td className="p-2 text-slate-900">{hc.vendor || hc.name || '-'}</td>
                          <td className="p-2 text-slate-600 font-mono">{hc.categoryCode || '-'}</td>
                          <td className="p-2 text-slate-700">{hc.categoryName || hc.name || '-'}</td>
                          <td className="p-2 text-right font-medium text-purple-700">
                            ${(hc.amount || 0).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </>
                  )}
                  
                  {/* Empty state */}
                  {selectedItems.length === 0 && holdingCosts.length === 0 && (
                    <tr>
                      <td colSpan={5} className="p-8 text-center text-slate-500">
                        No items selected for this draw request
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot className="bg-slate-100 border-t-2 border-slate-300">
                  <tr>
                    <td colSpan={4} className="p-3 text-right font-bold text-slate-700 uppercase">
                      Total Requested:
                    </td>
                    <td className="p-3 text-right font-bold text-[#1b4b8a] text-lg">
                      ${totalRequested.toLocaleString()}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            
            {/* Lien Guaranty Preview & Signature */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <h4 className="font-semibold text-amber-800 text-sm mb-2">Lien Guaranty</h4>
              <p className="text-xs text-amber-700 leading-relaxed">
                This is written evidence to Bank that there are no unpaid bills for labor that have been billed and 
                furnished for the construction, repairs or improvements on the above-described property (other than 
                bills being paid from this draw and materials or labor accrued but not yet billed). I/We further agree 
                to fully indemnify Bank against any and all possible payments or expense of legal action in regard to 
                mechanics liens that may be filed upon the above-described property...
              </p>
              
              {/* Signature Section */}
              <div className="mt-4 pt-3 border-t border-amber-200">
                <div className="flex items-center gap-2 mb-3">
                  <Checkbox
                    id="sign-document"
                    checked={signDocument}
                    onCheckedChange={(checked) => setSignDocument(checked)}
                    disabled={!userSignature}
                    data-testid="sign-document-checkbox"
                  />
                  <label 
                    htmlFor="sign-document" 
                    className={`text-sm font-medium ${userSignature ? 'text-amber-800' : 'text-amber-500'}`}
                  >
                    Sign this document electronically
                  </label>
                </div>
                
                {userSignature ? (
                  <div className="flex items-center gap-4">
                    <div className="flex-1 bg-white border border-amber-300 rounded p-2">
                      <p className="text-xs text-amber-600 mb-1">Your Saved Signature:</p>
                      <img 
                        src={userSignature} 
                        alt="Your signature" 
                        className="max-h-12 object-contain"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowSignaturePad(true)}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      >
                        <Pen className="w-4 h-4 mr-1" />
                        Change
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleDeleteSignature}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Remove
                      </Button>
                    </div>
                  </div>
                ) : showSignaturePad ? (
                  <div className="mt-2">
                    <SignaturePad 
                      onSave={handleSaveSignature}
                      onClear={() => {}}
                      width={350}
                      height={120}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowSignaturePad(false)}
                      className="mt-2 text-slate-500"
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowSignaturePad(true)}
                      className="border-amber-400 text-amber-700 hover:bg-amber-100"
                    >
                      <Pen className="w-4 h-4 mr-1" />
                      Sign Here
                    </Button>
                    <span className="text-xs text-amber-600">
                      Draw your signature to enable e-signing
                    </span>
                  </div>
                )}
                
                {signDocument && userSignature && (
                  <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                    <Pen className="w-3 h-3" />
                    Document will be signed with today&apos;s date ({formatDate(new Date())})
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {onSubmitToBank && (
          <div className="px-4 pb-1">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3">
              <div className="flex items-center gap-2 mb-1.5">
                <Building2 className="w-4 h-4 text-emerald-700" />
                <h4 className="font-semibold text-emerald-800 text-sm">Submit straight to your bank</h4>
              </div>
              <p className="text-xs text-slate-600 mb-2">
                Emails the DrawReady draw form (PDF) to your bank's construction loan department — sent via DrawReady,
                with <strong>replies going to your business email</strong>. No download-and-forward needed.
              </p>
              <label className="text-xs font-semibold text-slate-600">Loan department email (saved to this project)</label>
              <input
                value={lenderEmail}
                onChange={(e) => setLenderEmail(e.target.value)}
                placeholder="constructiondept@yourbank.com"
                className="mt-1 mb-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                data-testid="lender-email-input"
              />
              <label className="text-xs font-semibold text-slate-600">Replies go to (your email)</label>
              <input
                value={replyToEmail}
                onChange={(e) => setReplyToEmail(e.target.value)}
                placeholder="you@yourbusiness.com"
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                data-testid="reply-to-input"
              />
              <p className="text-[11px] text-slate-500 mt-1 mb-2">When the bank hits reply, it lands in this inbox (not DrawReady). We&apos;ll remember it for next time.</p>
              <label className="flex items-center gap-2 text-xs text-slate-600 mb-2">
                <input type="checkbox" checked={testMode} onChange={(e) => setTestMode(e.target.checked)} className="w-4 h-4 accent-emerald-600" />
                Send a test to my own email first
              </label>
              {testMode && (
                <input
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="you@yourbusiness.com"
                  className="mb-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  data-testid="test-email-input"
                />
              )}
              <Button
                onClick={() => onSubmitToBank({ lenderEmail, replyTo: replyToEmail, testEmail: testMode ? testEmail : null })}
                disabled={isSubmittingToBank || (selectedItems.length === 0 && holdingCosts.length === 0) || (!lenderEmail && !(testMode && testEmail))}
                className="w-full bg-[#0f2440] hover:bg-[#12283f]"
                data-testid="submit-to-bank-btn"
              >
                {isSubmittingToBank ? 'Sending…' : (testMode ? 'Send test draw email' : 'Submit draw to bank')}
              </Button>
            </div>
          </div>
        )}
        <DialogFooter className="p-4 border-t bg-slate-50 rounded-b-lg gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={onCompare}
            disabled={isComparing || (selectedItems.length === 0 && holdingCosts.length === 0)}
            className="border-blue-300 text-blue-700 hover:bg-blue-50"
            data-testid="compare-formats-btn"
          >
            {isComparing ? 'Building…' : 'Compare formats'}
          </Button>
          <Button
            onClick={onGenerate}
            disabled={isGenerating || (selectedItems.length === 0 && holdingCosts.length === 0)}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="view-draw-btn"
          >
            {isGenerating ? (
              <>Preparing…</>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                View draw
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DrawRequestPreviewModal;
